import { cn } from '@comps/lib/utils';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator } from '@comps/ui/Breadcrumb';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@comps/ui/dropdownmenu';
import { ASC, DESC, DriveItem, FILE, FOLDER, FetchItemsFn, LAST_MODIFIED, NAME, SIZE, SortDirection, SortField, TYPE } from '@dto/drive';
import { downloadDriveFile } from '@utils/driveApi';
import { TW_DrivePanel_BreadcrumbBar, TW_DrivePanel_BreadcrumbHome, TW_DrivePanel_BreadcrumbHomeIcon, TW_DrivePanel_BreadcrumbItemActive, TW_DrivePanel_BreadcrumbItemInactive, TW_DrivePanel_CellName, TW_DrivePanel_FileIcon, TW_DrivePanel_FolderIcon, TW_DrivePanel_MoreIcon, TW_DrivePanel_Container, TW_DrivePanel_EmptyState, TW_DrivePanel_FileList, TW_DrivePanel_MoreButton, TW_DrivePanel_SortHeaderIcon, TW_DrivePanel_SortIcon, TW_DrivePanel_Table, TW_DrivePanel_TableCell, TW_DrivePanel_TableCellAction, TW_DrivePanel_TableCellMuted, TW_DrivePanel_TableHead, TW_DrivePanel_TableHeadCell, TW_DrivePanel_TableRow } from '@utils/twClasses';
import { ArrowUp2, ArrowDown2, Document, Folder2, Home3, More } from 'iconsax-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModelStore } from 'src/store';


function formatSize(bytes: number | null): string {
    if (bytes === null || bytes === undefined) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDate(iso: string | null, locale: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(locale, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

interface SortHeaderProps {
    label: string;
    field: SortField;
    current: SortField;
    direction: SortDirection;
    onSort: (field: SortField) => void;
    className?: string;
}

function SortHeader({ label, field, current, direction, onSort, className }: SortHeaderProps) {
    const isActive = current === field;
    return (
        <th
            className={cn(TW_DrivePanel_TableHeadCell, className)}
            onClick={() => onSort(field)}
        >
            <span className={TW_DrivePanel_SortHeaderIcon}>
                {label}
                {isActive && (
                    direction === ASC
                        ? <ArrowUp2 className={TW_DrivePanel_SortIcon} />
                        : <ArrowDown2 className={TW_DrivePanel_SortIcon} />
                )}
            </span>
        </th>
    );
}

interface DrivePanelProps {
    wsKey: string;
    currentPath: string;
    onNavigate: (path: string) => void;
    fetchItems: FetchItemsFn;
}

function DrivePanel({ wsKey, currentPath, onNavigate, fetchItems }: DrivePanelProps) {
    const { t, i18n } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const [items, setItems] = useState<DriveItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [sortField, setSortField] = useState<SortField>(TYPE);
    const [sortDir, setSortDir] = useState<SortDirection>(DESC);

    useEffect(() => {
        setLoading(true);
        fetchItems(currentPath).then(items => {
            setItems(items);
            setLoading(false);
        });
    }, [fetchItems, currentPath]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => prev === ASC ? DESC : ASC);
        } else {
            setSortField(field);
            setSortDir(ASC);
        }
    };

    const sorted = useMemo(() => {
        return [...items].sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case NAME:
                    cmp = a.name.localeCompare(b.name);
                    break;
                case TYPE:
                    cmp = a.type.localeCompare(b.type);
                    break;
                case LAST_MODIFIED:
                    cmp = (a.lastModified ?? '').localeCompare(b.lastModified ?? '');
                    break;
                case SIZE:
                    cmp = (a.size ?? -1) - (b.size ?? -1);
                    break;
            }
            return sortDir === ASC ? cmp : -cmp;
        });
    }, [items, sortField, sortDir]);

    const handleItemDoubleClick = (item: DriveItem) => {
        if (item.type === FOLDER) {
            onNavigate(item.path);
        } else {
            downloadDriveFile(wsKey, item.path);
        }
    };

    // Build breadcrumb segments from currentPath ("Docs/Sub/Child/" → [{label:"Docs", path:"Docs/"}, ...])
    const breadcrumbSegments = currentPath
        .split('/')
        .filter(Boolean)
        .map((segment, idx, arr) => ({
            label: segment,
            path: arr.slice(0, idx + 1).join('/') + '/',
        }));

    return (
        <div className={TW_DrivePanel_Container}>
            {/* Breadcrumb */}
            <div className={TW_DrivePanel_BreadcrumbBar}>
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem
                            className={TW_DrivePanel_BreadcrumbHome}
                            onClick={() => onNavigate('')}
                        >
                            <Home3 className={TW_DrivePanel_BreadcrumbHomeIcon} />
                        </BreadcrumbItem>
                        {breadcrumbSegments.map((seg, idx) => (
                            <>
                                <BreadcrumbSeparator key={'sep-' + seg.path} />
                                <BreadcrumbItem
                                    key={seg.path}
                                    className={idx === breadcrumbSegments.length - 1
                                        ? TW_DrivePanel_BreadcrumbItemActive
                                        : TW_DrivePanel_BreadcrumbItemInactive}
                                    onClick={idx < breadcrumbSegments.length - 1 ? () => onNavigate(seg.path) : undefined}
                                >
                                    {seg.label}
                                </BreadcrumbItem>
                            </>
                        ))}
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            {/* File list */}
            <div className={TW_DrivePanel_FileList}>
                {loading ? (
                    <div className={TW_DrivePanel_EmptyState}>
                        {t('app.drive.loading')}
                    </div>
                ) : sorted.length === 0 ? (
                    <div className={TW_DrivePanel_EmptyState}>
                        {t('app.drive.empty')}
                    </div>
                ) : (
                    <table className={TW_DrivePanel_Table}>
                        <thead className={TW_DrivePanel_TableHead}>
                            <tr>
                                <SortHeader label={t('app.drive.col.name')} field={NAME} current={sortField} direction={sortDir} onSort={handleSort} className="w-1/2" />
                                <SortHeader label={t('app.drive.col.type')} field={TYPE} current={sortField} direction={sortDir} onSort={handleSort} className="w-1/6" />
                                <SortHeader label={t('app.drive.col.modified')} field={LAST_MODIFIED} current={sortField} direction={sortDir} onSort={handleSort} className="w-1/6" />
                                <SortHeader label={t('app.drive.col.size')} field={SIZE} current={sortField} direction={sortDir} onSort={handleSort} className="w-1/6" />
                                <th className="w-8" />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(item => (
                                <tr
                                    key={item.path}
                                    className={TW_DrivePanel_TableRow}
                                    onDoubleClick={() => handleItemDoubleClick(item)}
                                >
                                    <td className={TW_DrivePanel_TableCell}>
                                        <span className={TW_DrivePanel_CellName}>
                                            {item.type === FOLDER
                                                ? <Folder2 className={TW_DrivePanel_FolderIcon} style={{ color: wsColor }} />
                                                : <Document className={TW_DrivePanel_FileIcon} />
                                            }
                                            <span className={item.type === FOLDER ? 'font-medium' : ''}>{item.name}</span>
                                        </span>
                                    </td>
                                    <td className={TW_DrivePanel_TableCellMuted}>
                                        {item.type === FOLDER ? t('app.drive.type.folder', 'Dossier') : item.name.split('.').pop()?.toUpperCase() ?? '—'}
                                    </td>
                                    <td className={TW_DrivePanel_TableCellMuted}>{formatDate(item.lastModified, i18n.language)}</td>
                                    <td className={TW_DrivePanel_TableCellMuted}>{item.type === FOLDER ? '—' : formatSize(item.size)}</td>
                                    <td className={TW_DrivePanel_TableCellAction}>
                                        {item.type === FILE && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger className={TW_DrivePanel_MoreButton} onClick={e => e.stopPropagation()}>
                                                    <More className={TW_DrivePanel_MoreIcon} />
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem className="cursor-pointer" onClick={() => downloadDriveFile(wsKey, item.path)}>
                                                        {t('app.drive.action.download')}
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default DrivePanel;
